/**
 * Typed tenant UWB location API facade over the generated contract client.
 */

import { z } from 'zod';
import { locationUwbApi } from '../api/generated/locationUwb.api.js';
import {
  EventsQuerySchema,
  type AllTagsQuery,
  type AllTagsResponse,
  type EventsResponse,
  type HistoryQuery,
  type SiteAnchorHealthListResponse,
  type SiteOverviewListResponse,
  type SiteTagFleetListResponse,
  type SiteTagFleetTagsListQuery,
  type SiteTagFleetTagsListResponse,
  type TagCurrentStateListResponse,
  type TagHistoryResponse,
  type TenantIntegrationStatusResponse,
  type TenantSiteAnchorHealthListQuery,
  type TenantSiteTagFleetListQuery,
  type UwbCommandRequest,
  type UwbCommandResponse,
} from '@cnapse/shared/schemas.js';

class LocationUwbService {
  async getTagsCurrentState(siteId: string): Promise<TagCurrentStateListResponse> {
    return locationUwbApi.getTagsCurrent({ siteId }) as Promise<TagCurrentStateListResponse>;
  }

  async getTenantLocationIntegrationStatus(siteId: string): Promise<TenantIntegrationStatusResponse> {
    return locationUwbApi.getIntegrationStatus({ siteId }) as Promise<TenantIntegrationStatusResponse>;
  }

  async getAnchorsCurrentState(siteId: string): Promise<TagCurrentStateListResponse> {
    return locationUwbApi.getAnchorsCurrent({ siteId }) as Promise<TagCurrentStateListResponse>;
  }

  async getTagHistory(
    siteId: string,
    serialNumber: string,
    query: HistoryQuery,
  ): Promise<TagHistoryResponse> {
    return locationUwbApi.getTagHistory({ siteId, serialNumber }, query) as Promise<TagHistoryResponse>;
  }

  async getSiteEvents(
    siteId: string,
    query: z.infer<typeof EventsQuerySchema>,
  ): Promise<EventsResponse> {
    return locationUwbApi.getSiteEvents({ siteId }, query) as Promise<EventsResponse>;
  }

  async sendUwbCommand(siteId: string, cmd: UwbCommandRequest): Promise<UwbCommandResponse> {
    return locationUwbApi.sendCommand({ siteId }, cmd) as Promise<UwbCommandResponse>;
  }

  async getLocationSites(): Promise<SiteOverviewListResponse> {
    return locationUwbApi.listSites({}) as Promise<SiteOverviewListResponse>;
  }

  async getAllLocationTags(query: AllTagsQuery): Promise<AllTagsResponse> {
    return locationUwbApi.listAllTags(query) as Promise<AllTagsResponse>;
  }

  async getTenantSiteAnchorHealthList(
    query: TenantSiteAnchorHealthListQuery,
  ): Promise<SiteAnchorHealthListResponse> {
    return locationUwbApi.listSiteAnchorHealth(query) as Promise<SiteAnchorHealthListResponse>;
  }

  async getTenantSiteTagFleetList(
    query: TenantSiteTagFleetListQuery,
  ): Promise<SiteTagFleetListResponse> {
    return locationUwbApi.listSiteTagFleet(query) as Promise<SiteTagFleetListResponse>;
  }

  async getTenantSiteTagFleetTags(
    siteId: string,
    query?: SiteTagFleetTagsListQuery,
  ): Promise<SiteTagFleetTagsListResponse> {
    return locationUwbApi.listSiteTagFleetTags({ siteId }, query ?? {}) as Promise<SiteTagFleetTagsListResponse>;
  }
}

export const locationUwbService = new LocationUwbService();
